import { ref, onValue, get, set } from 'firebase/database';
import { realtimeDatabase } from '../database/firebase';

/** Firebase RTDB keys cannot contain . # $ [ ] / */
export function sanitizeFirebaseKey(value) {
  return String(value ?? 'unknown').replace(/[.#$/[\]]/g, '_');
}

/** Same org + session → same live board for all players. */
export function buildFirebaseSessionId(organizationId, sessionId) {
  const org = sanitizeFirebaseKey(organizationId || 'default');
  const sess = sanitizeFirebaseKey(sessionId);
  return `${org}__${sess}`;
}

export function sessionUsersRef(firebaseSessionId) {
  return ref(realtimeDatabase, `session/${firebaseSessionId}/users`);
}

export function userPuzzleRef(firebaseSessionId, firebaseUserId) {
  return ref(
    realtimeDatabase,
    `session/${firebaseSessionId}/users/${sanitizeFirebaseKey(firebaseUserId)}`
  );
}

export function getPlacedTileIndices(puzzleInstance) {
  if (!puzzleInstance?.getTiles) return [];
  const tiles = puzzleInstance.getTiles();
  const placed = [];
  const keys = Object.keys(tiles);
  for (let i = 0; i < keys.length; i += 1) {
    const entry = tiles[i];
    if (!entry || !entry[1]) continue;
    if (Number(keys[i]) === Number(entry[0])) {
      placed.push(i);
    }
  }
  return placed;
}

async function mergeUserPuzzleState(firebaseSessionId, firebaseUserId, payload) {
  if (!firebaseSessionId || !firebaseUserId) return;
  const userRef = userPuzzleRef(firebaseSessionId, firebaseUserId);
  const snap = await get(userRef);
  const current = snap.exists() ? snap.val() : {};
  await set(userRef, {
    ...current,
    ...payload,
    updatedAt: Date.now(),
  });
}

export async function registerUserPuzzleSession(
  firebaseSessionId,
  firebaseUserId,
  payload
) {
  if (!firebaseSessionId || !firebaseUserId) return;
  const userRef = userPuzzleRef(firebaseSessionId, firebaseUserId);
  const snap = await get(userRef);
  if (snap.exists()) {
    const current = snap.val();
    await set(userRef, {
      ...current,
      name: payload.name ?? current.name,
      puzzle_id: payload.puzzle_id ?? current.puzzle_id,
      totalTiles: payload.totalTiles ?? current.totalTiles,
      microRows: payload.microRows ?? current.microRows,
      microCols: payload.microCols ?? current.microCols,
      updatedAt: Date.now(),
    });
    return;
  }
  await set(userRef, {
    ...payload,
    placedTiles: [],
    completed: false,
    updatedAt: Date.now(),
  });
}

export async function writeUserPuzzleState(firebaseSessionId, firebaseUserId, payload) {
  await mergeUserPuzzleState(firebaseSessionId, firebaseUserId, payload);
}

export async function patchUserPuzzleState(firebaseSessionId, firebaseUserId, payload) {
  await mergeUserPuzzleState(firebaseSessionId, firebaseUserId, payload);
}

export function subscribeSessionUsers(firebaseSessionId, onData) {
  if (!firebaseSessionId) return () => {};
  const dbRef = sessionUsersRef(firebaseSessionId);
  return onValue(dbRef, (snapshot) => {
    onData(snapshot.val() || {});
  });
}

export function puzzleProgressPercent(userState) {
  if (!userState || typeof userState !== 'object') return 0;
  if (userState.completed) return 100;
  const total = Math.max(1, Math.floor(Number(userState.totalTiles)) || 1);
  const placed = Array.isArray(userState.placedTiles) ? userState.placedTiles.length : 0;
  return Math.min(100, Math.round((placed / total) * 100));
}

function livePuzzleUserScore(data) {
  if (!data || typeof data !== 'object') return -1;
  const placed = Array.isArray(data.placedTiles) ? data.placedTiles.length : 0;
  const completed = data.completed ? 1 : 0;
  const updatedAt = Number(data.updatedAt) || 0;
  const hasStartedSolving = placed > 0 || completed;
  // Active solvers beat idle assignees; more progress wins; then most recent activity.
  return (
    (hasStartedSolving ? 1e15 : 0) +
    completed * 1e12 +
    placed * 1e9 +
    updatedAt
  );
}

export function usersByPuzzleId(usersMap) {
  const byPiece = {};
  if (!usersMap || typeof usersMap !== 'object') return byPiece;
  Object.entries(usersMap).forEach(([, data]) => {
    const pieceId = Math.floor(Number(data?.puzzle_id));
    if (!Number.isFinite(pieceId) || pieceId < 1) return;
    const existing = byPiece[pieceId];
    if (!existing || livePuzzleUserScore(data) > livePuzzleUserScore(existing)) {
      byPiece[pieceId] = data;
    }
  });
  return byPiece;
}

/** All users grouped by puzzle_id (sorted: active solvers first). */
export function usersAllByPuzzleId(usersMap) {
  const byPiece = {};
  if (!usersMap || typeof usersMap !== 'object') return byPiece;
  Object.entries(usersMap).forEach(([userId, data]) => {
    const pieceId = Math.floor(Number(data?.puzzle_id));
    if (!Number.isFinite(pieceId) || pieceId < 1) return;
    if (!byPiece[pieceId]) byPiece[pieceId] = [];
    byPiece[pieceId].push({ userId, ...data });
  });
  Object.values(byPiece).forEach((players) => {
    players.sort((a, b) => livePuzzleUserScore(b) - livePuzzleUserScore(a));
  });
  return byPiece;
}
